import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import PublicProject from './pages/PublicProject';
import PublicTrack from './pages/PublicTrack';
import SharedLink from './pages/SharedLink';
import LeftPanel from './components/LeftPanel';
import MiddlePanel from './components/MiddlePanel';
import RightPanel from './components/RightPanel';
import PlayerBar from './components/PlayerBar';
import SharedPlayer from './components/SharedPlayer';
import NewProjectModal from './components/NewProjectModal';
import DeleteProjectModal from './components/DeleteProjectModal';
import ShareModal from './components/ShareModal';
import { projectTarget, trackTarget } from './shareTarget';
import ProjectInsights from './components/ProjectInsights';
import PasswordGate from './components/PasswordGate';
import Login from './components/Login';
import Onboarding from './components/Onboarding';
import SelectArtistPopup from './components/SelectArtistPopup';
import SettingsPopup from './components/SettingsPopup';
import icon from './icon.png';
import User from './models/User';
import Artist from './models/Artist';
import Project from './models/Project';
import firebase from './firebaseConfig';
import { loadSession, saveSession } from './persistence';
import { cloneInstance, buildSharedProject, buildOptimisticSharedProject } from './utils';
import { shareLinkErrorMessage } from './shareTarget';
import { memberHomeURL, sharedTrackAudioFetcher, buildMemberProjectFromRedeem } from './shareRouting';
import { InboxKeys } from './rtdbKeys';
import { subscribeUploads, getProjectUploads } from './uploads';
import { track as trackEvent, setUser as setAnalyticsUser } from './analytics';

function HomeShell() {
    const [selectedArtist, setSelectedArtist] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [projects, setProjects] = useState([]);
    const [tracks, setTracks] = useState([]);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [playbackTracks, setPlaybackTracks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loggedIn, setLoggedIn] = useState(false);
    const [onboardingUser, setOnboardingUser] = useState(null);
    const [isSelectArtistPopupVisible, setIsSelectArtistPopupVisible] = useState(false);
    const [isSettingsPopupVisible, setIsSettingsPopupVisible] = useState(false);
    const [isNewProjectModalVisible, setIsNewProjectModalVisible] = useState(false);
    const [shareTarget, setShareTarget] = useState(null);
    const [insightsProject, setInsightsProject] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [tracksLoading, setTracksLoading] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [passwordPromptSlug, setPasswordPromptSlug] = useState(null);
    const [passwordError, setPasswordError] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Re-render when the in-flight upload store changes.
    const [uploadsTick, bumpUploads] = useReducer((n) => n + 1, 0);
    useEffect(() => subscribeUploads(bumpUploads), []);

    const playerRef = useRef();
    const pendingRestoreRef = useRef(null);
    const selectedProjectRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const setupProjectFromURL = async () => {
            const params = new URLSearchParams(window.location.search);
            const encodedArtistId = params.get('aid');
            const encodedProjectId = params.get('pid');
            if (encodedArtistId && encodedProjectId) {
                const artistId = atob(encodedArtistId);
                const projectId = atob(encodedProjectId);
                const project = await Project.getProjectById(artistId, projectId);
                setSelectedProject(project);
            }
        };

        const handleAuthStateChange = async (user) => {
            if (user) {
                setAnalyticsUser(user);
                await User.ensureUserRecord(user);
                await handleLoginSuccess(user);
                await setupProjectFromURL();
            } else {
                setAnalyticsUser(null);
                setLoggedIn(false);
                setOnboardingUser(null);
                await setupProjectFromURL();
            }
            setLoading(false);
        };

        const unsubscribe = firebase.auth().onAuthStateChanged(handleAuthStateChange);
        return () => unsubscribe();
    }, []);

    useEffect(() => { selectedProjectRef.current = selectedProject; }, [selectedProject]);

    useEffect(() => {
        if (!selectedArtist) {
            setProjects([]);
            return;
        }
        let cancelled = false;
        selectedArtist.getSnapshotOfProjects().then((p) => {
            if (cancelled) return;
            setProjects(p);
            // Ref read so a concurrent click-driven setSelectedProject isn't clobbered.
            if (selectedProjectRef.current) return;
            const restoreId = pendingRestoreRef.current?.projectId;
            const restored = restoreId && p.find((pp) => pp.id === restoreId);
            const target = restored || p[0];
            if (target) setSelectedProject(target);
        }).catch(() => {
            if (!cancelled) setProjects([]);
        });
        return () => { cancelled = true; };
    }, [selectedArtist]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!selectedProject) {
            setTracks([]);
            setTracksLoading(false);
            return;
        }
        if (selectedProject.isShared) return; // tracks set by handleOpenShared
        // Seed with in-flight uploads so a folder-drop import shows immediately.
        setTracks(getProjectUploads(selectedProject.id).tracks);
        setTracksLoading(true);
        let first = true;
        const unsubscribe = selectedProject.subscribeToTracks((serverTracks) => {
            setTracks((prev) => {
                const serverIds = new Set(serverTracks.map((t) => t.id));
                // Keep optimistic rows for uploads not yet written to RTDB
                // (those carry a local blob URL; server rows don't).
                const pending = prev.filter((t) => t.localURL && !serverIds.has(t.id));
                return [...serverTracks, ...pending];
            });
            if (first) {
                setTracksLoading(false);
                first = false;
            }
        });
        return unsubscribe;
    }, [selectedProject]);

    // Mirror the open project's live count/duration onto its sidebar row.
    useEffect(() => {
        if (!selectedProject || selectedProject.isShared) return;
        const count = tracks.length;
        const duration = tracks.reduce((s, t) => s + (t?.duration || 0), 0);
        setProjects((prev) => {
            const i = prev.findIndex((p) => p.id === selectedProject.id);
            if (i < 0) return prev;
            const p = prev[i];
            if (p.numberOfTracks === count && p.duration === duration) return prev;
            const next = prev.slice();
            next[i] = cloneInstance(p, { numberOfTracks: count, duration });
            return next;
        });
    }, [tracks, selectedProject]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Delete' && e.key !== 'Backspace') return;
            if (!selectedProject || selectedProject.isShared || deleteTarget) return;
            const el = e.target;
            const tag = el?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return;
            e.preventDefault();
            setDeleteTarget(selectedProject);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedProject, deleteTarget]);

    // uploadsTick is the intentional invalidation key — the upload store is
    // mutable, so its identity can't drive the memo.
    const uploadProgress = useMemo(
        () => getProjectUploads(selectedProject?.id).progress,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [uploadsTick, selectedProject],
    );

    useEffect(() => {
        const restore = pendingRestoreRef.current;
        if (!restore || !restore.trackId || tracks.length === 0 || !selectedProject) return;
        if (restore.projectId && restore.projectId !== selectedProject.id) {
            pendingRestoreRef.current = null;
            return;
        }
        const track = tracks.find((t) => t.id === restore.trackId);
        if (track && playerRef.current?.loadAndPause) {
            setPlaybackTracks(tracks);
            playerRef.current.loadAndPause(track, restore.position || 0);
        }
        pendingRestoreRef.current = null;
    }, [tracks, selectedProject]);

    useEffect(() => {
        if (selectedArtist?.id) saveSession({ artistId: selectedArtist.id });
    }, [selectedArtist?.id]);

    useEffect(() => {
        if (selectedProject?.id) saveSession({ projectId: selectedProject.id });
    }, [selectedProject?.id]);

    useEffect(() => {
        saveSession({ trackId: currentTrack?.id || null });
    }, [currentTrack?.id]);

    useEffect(() => {
        if (loading || onboardingUser) return;
        const path = location.pathname;
        if (loggedIn && path === '/login') {
            navigate('/home', { replace: true });
        } else if (!loggedIn && path !== '/login') {
            navigate('/login', { replace: true });
        }
    }, [loggedIn, loading, onboardingUser, location.pathname, navigate]);

    useEffect(() => {
        if (!loading && loggedIn && location.pathname === '/home') {
            trackEvent('home_shown', { surface: 'home' });
        }
    }, [loading, loggedIn, location.pathname]);

    useEffect(() => {
        if (loading || !loggedIn) return;
        const params = new URLSearchParams(location.search);
        const slug = params.get('shared');
        const token = params.get('sharedToken');
        if (slug) {
            if (selectedProject?.isShared && selectedProject?.slug === slug) return;
            handleOpenShared(slug);
            return;
        }
        if (token) {
            if (selectedProject?.isShared && selectedProject?.token === token) return;
            handleOpenSharedToken(token, undefined, location.state?.redeemResult);
        }
    }, [loading, loggedIn, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLoginSuccess = async (user) => {
        const loggedInUser = await User.getUserById(user.uid);
        // Onboarding parity with iOS: a first name and an artist are both
        // required before the app is reachable. Last name is optional.
        const needsName = !loggedInUser.firstName?.trim();
        const needsArtist = loggedInUser.artistIds.length === 0;
        if (needsName || needsArtist) {
            setOnboardingUser(loggedInUser);
            return;
        }
        const saved = loadSession();
        const targetArtistId = (saved.artistId && loggedInUser.artistIds.includes(saved.artistId))
            ? saved.artistId
            : loggedInUser.artistIds[0];
        const artist = await Artist.getArtistById(targetArtistId);
        pendingRestoreRef.current = saved;
        // Clear onboarding and flip loggedIn in one commit — no await between.
        // A render where onboardingUser is null but loggedIn is still false
        // bounces to /login via the redirect effect, dropping the ?shared(Token)
        // param + redeem state a share link carried in.
        setOnboardingUser(null);
        setSelectedArtist(artist);
        setLoggedIn(true);
    };

    const handleOnboardingComplete = async () => {
        const user = firebase.auth().currentUser;
        // handleLoginSuccess clears onboardingUser itself — clearing it here
        // first reopens the /login-bounce window described there.
        if (user) await handleLoginSuccess(user);
        else setOnboardingUser(null);
    };

    const playTrack = (track, queue) => {
        if (Array.isArray(queue)) setPlaybackTracks(queue);
        setCurrentTrack(track);
    };

    const handleArtistSelect = (artist) => {
        pendingRestoreRef.current = null;
        setSelectedArtist(artist);
        setSelectedProject(null);
        setIsSelectArtistPopupVisible(false);
        setMobileMenuOpen(false);
    };

    // The currently selected artist was left or deleted from the picker.
    // Switch to whatever remains (or none) without closing the popup.
    const handleArtistDeparted = (nextArtist) => {
        pendingRestoreRef.current = null;
        setSelectedArtist(nextArtist || null);
        setSelectedProject(null);
    };

    const sharedAudioFetcher = useCallback(
        (trackId) => sharedTrackAudioFetcher(selectedProject)(trackId),
        [selectedProject?.token, selectedProject?.slug, selectedProject?.isShared],
    );

    const handleSelectProject = (project) => {
        pendingRestoreRef.current = null;
        setSelectedProject(project);
        setMobileMenuOpen(false);
        if (project?.id) {
            trackEvent('project_opened', {
                project_id: project.id,
                surface: 'home',
                source: 'tap',
            });
        }
    };

    // setSelectedProject paired with a synchronous ref write. The shared-link
    // openers settle a redeem promise that races getSnapshotOfProjects' default
    // pick; the selectedProject effect lags a commit behind, so the ref must be
    // updated synchronously for that race to resolve to the shared project.
    const selectProject = (project) => {
        selectedProjectRef.current = project;
        setSelectedProject(project);
    };

    const tryOpenShared = async (slug, password, preview) => {
        if (!slug) return;
        if (preview && preview.artistId && preview.projectId) {
            pendingRestoreRef.current = null;
            selectProject(buildOptimisticSharedProject(preview, { slug }));
            setTracks([]);
            setCurrentTrack(null);
            setTracksLoading(true);
            setMobileMenuOpen(false);
        }
        try {
            const fn = firebase.functions().httpsCallable('redeemProjectLink');
            const res = await fn(password ? { slug, password } : { slug });
            const data = res?.data;
            if (!data?.project) {
                setTracksLoading(false);
                return;
            }
            if (data.isArtistMember && data.artistId && data.projectId) {
                const { artist, project } = await buildMemberProjectFromRedeem(data);
                pendingRestoreRef.current = null;
                setSelectedArtist(artist);
                selectProject(project);
                setTracks([]);
                setCurrentTrack(null);
                setTracksLoading(false);
                setMobileMenuOpen(false);
                setPasswordPromptSlug(null);
                setPasswordError('');
                navigate(memberHomeURL(data.artistId, data.projectId), { replace: true });
                return;
            }
            const built = buildSharedProject({ slug }, data);
            pendingRestoreRef.current = null;
            selectProject(built.project);
            setTracks(built.tracks);
            setCurrentTrack(null);
            setTracksLoading(false);
            setMobileMenuOpen(false);
            setPasswordPromptSlug(null);
            setPasswordError('');
        } catch (err) {
            setTracksLoading(false);
            const reason = err?.details?.reason;
            if (reason === 'password-required') {
                setPasswordPromptSlug(slug);
                setPasswordError(password ? 'Wrong password.' : '');
                return;
            }
            if (reason === 'account-required') {
                alert('This project requires a specific account. Sign in with the right account first.');
                return;
            }
            console.error('Failed to open shared project', slug, err);
            alert(shareLinkErrorMessage(err));
        }
    };

    const handleOpenShared = (slug, preview) => tryOpenShared(slug, null, preview);

    const tryOpenSharedToken = async (token, preview, prefetchedResult) => {
        if (!token) return;
        if (preview && preview.artistId && preview.projectId) {
            pendingRestoreRef.current = null;
            selectProject(buildOptimisticSharedProject(preview, { token }));
            setTracks([]);
            setCurrentTrack(null);
            setTracksLoading(true);
            setMobileMenuOpen(false);
        }
        try {
            let data = prefetchedResult;
            if (!data) {
                const fn = firebase.functions().httpsCallable('redeemShareLink');
                const res = await fn({ token });
                data = res?.data;
            }
            if (data?.targetType !== 'project' || !data?.payload?.project) {
                setTracksLoading(false);
                navigate(`/s/${encodeURIComponent(token)}`, { replace: true });
                return;
            }
            if (data.isArtistMember && data.artistId && data.projectId) {
                const { artist, project } = await buildMemberProjectFromRedeem(data);
                pendingRestoreRef.current = null;
                setSelectedArtist(artist);
                selectProject(project);
                setTracks([]);
                setCurrentTrack(null);
                setTracksLoading(false);
                setMobileMenuOpen(false);
                navigate(memberHomeURL(data.artistId, data.projectId), { replace: true });
                return;
            }
            const built = buildSharedProject({ token }, data);
            pendingRestoreRef.current = null;
            selectProject(built.project);
            setTracks(built.tracks);
            setCurrentTrack(null);
            setTracksLoading(false);
            setMobileMenuOpen(false);
        } catch (err) {
            setTracksLoading(false);
            console.error('Failed to open shared token', token, err);
            navigate(`/s/${encodeURIComponent(token)}`, { replace: true });
        }
    };

    const handleOpenSharedToken = (token, preview, prefetchedResult) =>
        tryOpenSharedToken(token, preview, prefetchedResult);

    const handleSearchSelect = async (hit) => {
        const artistId = hit?.metadata?.artistId;
        const projectId = hit?.metadata?.projectId;
        if (!artistId) return;
        // Shared hits route through redeem so revoked/expired surface
        // correctly and the workspace doesn't flip to the sharer's artist.
        if (hit.isShared && projectId) {
            const uid = firebase.auth().currentUser?.uid;
            if (uid) {
                try {
                    const inboxSnap = await firebase.database()
                        .ref('users').child(uid)
                        .child(InboxKeys.openedShareLinks).child(InboxKeys.global)
                        .once('value');
                    const inbox = inboxSnap.val() || {};
                    const slug = Object.keys(inbox).find((k) => {
                        const e = inbox[k] || {};
                        return e[InboxKeys.artistId] === artistId
                            && e[InboxKeys.projectId] === projectId;
                    });
                    if (slug) {
                        await tryOpenShared(slug, null, {
                            artistId, projectId,
                            name: hit.title,
                            imageUrl: null,
                        });
                        return;
                    }
                } catch { /* fall through */ }
            }
        }
        try {
            const artist = await Artist.getArtistById(artistId);
            setSelectedArtist(artist);
            if (projectId) {
                const project = await Project.getProjectById(artistId, projectId);
                setSelectedProject(project);
            }
        } catch { /* ignore */ }
    };

    const handleCreateProject = async (name) => {
        if (!selectedArtist) throw new Error('Pick an artist first.');
        const project = await Project.create(selectedArtist, name);
        trackEvent('project_created', { project_id: project.id, surface: 'home' });
        setProjects((prev) => [project, ...prev]);
        setSelectedProject(project);
        setIsNewProjectModalVisible(false);
    };

    const handleProjectsCreated = (newProjects) => {
        if (!newProjects?.length) return;
        newProjects.forEach((p) =>
            trackEvent('project_created', { project_id: p.id, surface: 'folder_drop' }),
        );
        setProjects((prev) => [...newProjects, ...prev]);
    };

    const handleConfirmDeleteProject = async () => {
        const project = deleteTarget;
        if (!project) return;
        await project.delete();
        trackEvent('project_deleted', { project_id: project.id });
        setProjects((prev) => prev.filter((p) => p.id !== project.id));
        setSelectedProject((prev) => (prev?.id === project.id ? null : prev));
        setDeleteTarget(null);
    };

    const handleRenameProject = async (project, newName) => {
        const trimmed = (newName || '').trim();
        if (!trimmed || trimmed === project.name) return;
        await firebase.database()
            .ref('artists').child(project.artist.id)
            .child('projects').child(project.id)
            .child('metadata').update({
                name: trimmed,
                'time modified': Date.now(),
            });
        const renamed = cloneInstance(project, { name: trimmed });
        setProjects((prev) => prev.map((p) => (p.id === renamed.id ? renamed : p)));
        setSelectedProject((prev) => (prev?.id === renamed.id ? renamed : prev));
    };

    const handleRenameTrack = async (track, newName) => {
        const trimmed = (newName || '').trim();
        if (!trimmed || trimmed === track.name) return;
        await firebase.database()
            .ref('artists').child(track.project.artist.id)
            .child('projects').child(track.project.id)
            .child('tracks').child(track.id)
            .child('metadata').child('name').set(trimmed);
        const renamed = cloneInstance(track, { name: trimmed });
        setTracks((prev) => prev.map((t) => (t.id === renamed.id ? renamed : t)));
        setCurrentTrack((prev) => (prev?.id === renamed.id ? renamed : prev));
    };

    if (loading) {
        return (
            <div className="splash-screen">
                <img src={icon} alt="Demodeck Logo" className="splash-logo" />
            </div>
        );
    }

    if (onboardingUser) {
        return <Onboarding user={onboardingUser} onComplete={handleOnboardingComplete} />;
    }

    if (location.pathname === '/login' || (!loggedIn && !selectedProject)) {
        return <Login onLoginSuccess={() => {}} />;
    }

    return (
        <div className={`app-shell theme-dark ${mobileMenuOpen ? 'mobile-menu-open' : ''} ${selectedProject?.isShared ? 'has-floating-player' : ''}`}>
            <div className="app-body three-pane">
                <LeftPanel
                    selectedArtist={selectedArtist}
                    onPickArtist={() => { setIsSelectArtistPopupVisible(true); setMobileMenuOpen(false); }}
                    projects={projects}
                    selectedProject={selectedProject}
                    onSelectProject={handleSelectProject}
                    onSearchSelect={(hit) => { handleSearchSelect(hit); setMobileMenuOpen(false); }}
                    onCreateProject={() => { setIsNewProjectModalVisible(true); setMobileMenuOpen(false); }}
                    onProjectsCreated={handleProjectsCreated}
                    onRenameProject={handleRenameProject}
                    onOpenShared={handleOpenShared}
                    onOpenSharedToken={handleOpenSharedToken}
                    onSettings={() => { setIsSettingsPopupVisible(true); setMobileMenuOpen(false); }}
                />
                <div
                    className="mobile-menu-backdrop"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-hidden="true"
                />
                <MiddlePanel
                    project={selectedProject}
                    tracks={tracks}
                    onPlay={() => {
                        const player = playerRef.current;
                        if (!player) return;
                        const sameProject = currentTrack?.project?.id === selectedProject?.id;
                        if (sameProject && typeof player.togglePlay === 'function') {
                            player.togglePlay();
                        } else if (tracks.length > 0) {
                            playTrack(tracks[0], tracks);
                        }
                    }}
                    isPlaying={isPlaying}
                    onRenameProject={handleRenameProject}
                    readOnly={selectedProject?.isShared && selectedProject?.permission !== 'editor'}
                />
                <RightPanel
                    project={selectedProject}
                    tracks={tracks}
                    setTracks={setTracks}
                    currentTrack={currentTrack}
                    setCurrentTrack={(t) => playTrack(t, tracks)}
                    getCurrentPosition={() => playerRef.current?.getCurrentPosition?.() || 0}
                    onSeek={(s) => playerRef.current?.seekTo?.(s)}
                    onRenameProject={handleRenameProject}
                    onRenameTrack={handleRenameTrack}
                    onOpenMenu={() => setMobileMenuOpen(true)}
                    readOnly={selectedProject?.isShared && selectedProject?.permission !== 'editor'}
                    onShare={(p) => setShareTarget(projectTarget(p))}
                    onShareTrack={(t) => setShareTarget(trackTarget(t))}
                    onInsights={(p) => setInsightsProject(p)}
                    onProjectsCreated={handleProjectsCreated}
                    onDeleteProject={(p) => setDeleteTarget(p)}
                    uploadProgress={uploadProgress}
                    tracksLoading={tracksLoading}
                />
            </div>

            {selectedProject?.isShared ? (
                <SharedPlayer
                    ref={playerRef}
                    tracks={tracks}
                    currentTrack={currentTrack}
                    setCurrentTrack={setCurrentTrack}
                    audioFetcher={sharedAudioFetcher}
                    projectId={selectedProject.id}
                    albumArtUrl={selectedProject.imageUrl}
                    projectName={selectedProject.name}
                    onPlayingChange={setIsPlaying}
                />
            ) : (
                <PlayerBar
                    ref={playerRef}
                    tracks={playbackTracks}
                    currentTrack={currentTrack}
                    setCurrentTrack={setCurrentTrack}
                    onPositionUpdate={(seconds) => saveSession({ position: seconds })}
                    onPlayingChange={setIsPlaying}
                />
            )}

            {isSelectArtistPopupVisible && (
                <SelectArtistPopup
                    onSelect={handleArtistSelect}
                    onClose={() => setIsSelectArtistPopupVisible(false)}
                    currentArtist={selectedArtist}
                    onArtistDeparted={handleArtistDeparted}
                />
            )}
            {isSettingsPopupVisible && (
                <SettingsPopup onClose={() => setIsSettingsPopupVisible(false)} />
            )}
            {isNewProjectModalVisible && (
                <NewProjectModal
                    artist={selectedArtist}
                    onCreate={handleCreateProject}
                    onClose={() => setIsNewProjectModalVisible(false)}
                />
            )}
            {deleteTarget && (
                <DeleteProjectModal
                    project={deleteTarget}
                    onConfirm={handleConfirmDeleteProject}
                    onClose={() => setDeleteTarget(null)}
                />
            )}
            {passwordPromptSlug && (
                <div
                    className="modal-backdrop"
                    onClick={() => { setPasswordPromptSlug(null); setPasswordError(''); }}
                >
                    <div onClick={(e) => e.stopPropagation()}>
                        <PasswordGate
                            onSubmit={(pw) => tryOpenShared(passwordPromptSlug, pw)}
                            errorMessage={passwordError}
                        />
                    </div>
                </div>
            )}
            {shareTarget && (
                <ShareModal
                    target={shareTarget}
                    onClose={() => setShareTarget(null)}
                />
            )}
            {insightsProject && (
                <ProjectInsights
                    project={insightsProject}
                    onClose={() => setInsightsProject(null)}
                />
            )}
        </div>
    );
}

function App() {
    return (
        <Routes>
            <Route path="/p/:slug" element={<PublicProject />} />
            <Route path="/t/:slug" element={<PublicTrack />} />
            <Route path="/s/:token" element={<SharedLink />} />
            <Route path="*" element={<HomeShell />} />
        </Routes>
    );
}

export default App;
